import React from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import Col from 'react-bootstrap/Col';
import properties from '../../../modules/ajio-util/properties';
import { hideModal } from '../../../actions/modal';
import ProductSizeUpdation from './ProductSizeUpdation';

import { sendEvent } from '../../../utils/dataGrinch.js';
import { getPageInfo } from '../../../utils/pageInfo.js';
import { calculatePriceInfo } from '../../../utils/products.js';
import cleverTapPushEvent from '../../../modules/ajio-util/cleverTap/eventUtils';
import CLEVERTAP_EVENT_NAMES from '../../../modules/ajio-util/cleverTap/eventConstants';
import {
  getNewSizeRevampFlag,
  getShowBrandSizeFlag,
  getLuxeFlags
} from '../../../Flags/selectors.js';
import { getCurrentStore } from '../../../refresh/selectors/metaData';
import { getProductItemForCart, gtmEventPush, segmentEventPush } from '../../../modules/ga/GTMdatalayer';
import luxeScreen from '../../../refresh/constants/luxeScreenType';
import { readCookie } from '../../../components/common/Cookie';
// Desktop cart popup — uses the desktop SLA component. Deep imports keep the
// connected container (and its redux wiring) out of this bundle.
import DeliverySLA from '../../../components/desktop/DeliverySLA/DeliverySLA';
import {
  findDeliveryChoice,
  mapDeliveryChoice,
  toRawSlaOption,
  deliveryChoicesToSizeVariants,
  sizeVariantSelectedOf
} from '../../../components/desktop/DeliverySLA/mapDeliveryChoices';
// Wishlist renders the connected desktop PDP container, so the SLA block is
// driven by exactly the same store reads and mapping the PDP uses.
import DeliverySLAContainer from '../../../components/desktop/DeliverySLA/DeliverySLAContainer';
// B2C seller card — same component the desktop PDP mounts, hosted here with the
// popup's own product / seller / card set.
import B2cSellerDeliverySLA from '../../../components/desktop/DeliverySLA/B2cSellerDeliverySLA';
// Stands in for the card when there is no pincode / address to quote against.
import B2cSellerNoLocation from '../../../components/desktop/DeliverySLA/B2cSellerNoLocation';
import { sizeLabelOf } from '../../../components/desktop/DeliverySLA/B2cSellerModal';
// TODO - Remove this mock data used, along with the fallbacks below.
import {
  B2C_MOCK_POPUP_SELLER_NAME,
  B2C_MOCK_POPUP_SLA_OPTIONS
} from '../../../components/desktop/DeliverySLA/b2cSellerMockData';
import { getDeliveryChoices, selectProductSize } from '../../actions/cart';
// Aliased: `selectSize` is already a state key in this component's render.
import { selectSize as selectPdpSizeAction } from '../../../actions/productData';
import { fetchDeliveryDetailsV2, hasVisibleDeliveryLocation } from '../../../edd/actions';
import { getPincodeFromCookie } from '../../../edd/utils/utils';
import { ModalTypes } from '../../../constants/index.js';
import {
  getMultiSellerSlaFlag,
  getMultiSellerSlaConfig,
  getB2cSellerConfig,
  getB2cSellerFlag
} from '../../../refresh/selectors/featureFlagsSelector';
import {
  B2C_MAX_DEFAULT_SELLERS,
  SLA_SURFACE,
  pickSlaDeliveryNote,
  sellerNameForSize
} from '../../../refresh/molecules/DeliverySLA/constants';
import { selectSlaOptions } from '../../../components/desktop/DeliverySLA/selectSla';
/*
 * The same serviceability read the cards are mapped with. The local copy this
 * replaces treated a missing flag as "not deliverable" and never looked at the
 * nested productDetails entry, so an option the cards showed as deliverable was
 * skipped here — leaving the update with no priceType and no seller.
 */
import { isSlaOptionServiceable } from '../../../refresh/molecules/DeliverySLA/mapDeliverySLA';

const INFO_ICON = `${properties.getProperty('imageBaseUrl')}/static/img/multiseller-info-icon.svg`;
const INFO_ICON_LUXE = `${properties.getProperty(
  'imageBaseUrl'
)}/static/img/multiseller-info-icon-luxe.svg`;

// Same copy used when Amplitude omits oneSlaDeliveryNote.
const SLA_DELIVERY_NOTE = 'Delivery time may vary based on items in your bag';

/**
 * Card thumbnail for the B2C seller-list modal, off the popup's own line item —
 * the listing-grid asset, same precedence the PDP modal uses.
 */
const popupThumbnailOf = (itemData = {}) => {
  const { images = [] } = itemData;
  const match =
    images.find(image => image?.format === 'product') ||
    images.find(image => image?.format === 'productGrid3ListingImage') ||
    images.find(image => image?.format === 'thumbnail') ||
    images[0];
  return match?.url || '';
};

/**
 * availableQuantity of the delivery card in play — the one the user picked in
 * this popup, else the API's preselected card, else the first serviceable one.
 * Returns undefined when there are no options (or no numeric quantity), so the
 * caller keeps the size-level stock it already had.
 */
const slaStockLevelOf = (viewModel, userRawPriceType) => {
  const options = viewModel?.options || [];
  if (!options.length) return undefined;
  const picked =
    (userRawPriceType && options.find(option => option?.rawPriceType === userRawPriceType)) ||
    options.find(option => option?.isSelected && option?.serviceable) ||
    options.find(option => option?.serviceable) ||
    options[0];
  const level = Number(picked?.stock?.level);
  return Number.isFinite(level) ? level : undefined;
};

/**
 * Point `isSelected` at the option the user picked in this popup.
 *
 * The cart maps its view model inline on every render, so each render hands
 * DeliverySLA a different object — and that component scopes a manual pick to
 * the exact view model it was made on, deliberately, so a fresh scopedView
 * response wins. Selecting a card here calls setState, which re-renders and
 * remaps, so the pick was dropped every time and the radio jumped back to the
 * API's choice. Folding the pick into the model keeps the model the single
 * source of truth for what is selected.
 *
 * Left alone when the picked priceType is not among this size's serviceable
 * options — after a size change the API's own selection should stand.
 */
const withUserSlaSelection = (viewModel, userRawPriceType) => {
  if (!viewModel || !userRawPriceType) return viewModel;
  const options = viewModel.options || [];
  const picked = options.find(
    option => option?.rawPriceType === userRawPriceType && option?.serviceable
  );
  if (!picked) return viewModel;
  return {
    ...viewModel,
    options: options.map(option => ({
      ...option,
      isSelected: option === picked
    }))
  };
};

/**
 * Same idea as withUserSlaSelection, for the B2C raw card set. Selecting a card
 * setStates this popup, which remaps deliveryChoices into a new array whose
 * API `isSelected` is still the default — so the radio jumped back unless the
 * pick is written onto the options the card maps.
 */
const withUserB2cRawSelection = (rawOptions, picked) => {
  if (!Array.isArray(rawOptions) || !picked) return rawOptions;
  const sellerId = picked.sellerId || '';
  const rawPriceType = picked.rawPriceType || picked.priceType || '';
  const matchIndex = rawOptions.findIndex(option => {
    if (sellerId && option?.sellerId) return option.sellerId === sellerId;
    if (rawPriceType) return option?.priceType === rawPriceType;
    return false;
  });
  if (matchIndex < 0) return rawOptions;
  return rawOptions.map((option, index) => ({
    ...option,
    isSelected: index === matchIndex
  }));
};

const { cart: { screenType = '', screenName = '' } = {}, luxeStore = '' } = luxeScreen;
class ProductUpdationPopup extends React.Component {
  constructor(props) {
    super(props);
    this.config = {
      min: 1,
      max: 10
    };
    this.state = {
      currentCount: 0,
      oldQuantity: 0,
      selectSize: false,
      // Cart-side delivery options. The wishlist host fetches them itself and
      // passes them in, so this stays empty there. The cart host prefetches
      // them for B2C — it holds the popup closed until the call answers — so
      // seed from that rather than fetching the same payload again.
      cartDeliveryChoices: Array.isArray(props.deliveryChoices) ? props.deliveryChoices : [],
      cartChoicesLoading: false,
      cartChoicesError: false,
      // Multiseller: user's SLA pick in the modal (overrides API isSelected).
      userPriceType: '',
      slaSelectedPriceType: '',
      // B2C: the seller card the user is on, for its priceType and seller id.
      b2cSelectedOption: null,
      // Intentional edits only — auto qty reset on size/seller is not a qty edit.
      userChangedSize: false,
      userChangedQty: false,
      userChangedSeller: false,
      // B2C: id of the seller picked here. Sent with the item update, since B2C
      // attributes an entry by seller id rather than by priceType.
      userSellerId: ''
    };
    this.handleSlaSelect = this.handleSlaSelect.bind(this);
    this.textContent = {
      selectQuantity: 'Select quantity'
    };
  }

  componentWillMount() {
    const { quantity = 0, newDesignLuxe, itemData, wishlistPage = false } = this.props;

    this.setState({ currentCount: quantity, oldQuantity: quantity });
    // The events below describe a cart line-item being edited. The wishlist host
    // opens the same popup to add a brand new item, so they are skipped there —
    // that flow pushes its own closet events (see wishlistAddToBag).
    if (wishlistPage) return;
    if (newDesignLuxe) {
      gtmEventPush({
        event: 'size_change_cart',
        event_category: 'bag interactions',
        event_action: 'size change',
        store_type: 'luxe',
        screen_name: screenName,
        screen_type: screenType
      });
    }

    segmentEventPush({
       event: 'Size Updated',       
       screen_name: screenName,
       screen_type: screenType,
       cart_id: readCookie('C'),
       products:[
        {
          ...getProductItemForCart(itemData)
        }
       ]

    })

  }

  componentDidMount() {
    this.fetchDeliveryChoicesIfNeeded();
    this.syncWishlistSizeToPdpState();
    this.fetchB2cSlaIfNeeded();
  }

  componentDidUpdate(prevProps) {
    const { size = '', slaSkuId = '' } = this.props;
    if (size !== (prevProps.size || '')) {
      this.syncWishlistSizeToPdpState();
    }
    // Every size change is its own scopedView call — the response is per sku.
    if (slaSkuId !== (prevProps.slaSkuId || '')) {
      this.fetchB2cSlaIfNeeded();
    }
  }

  /**
   * B2C Delivery SLA — the same scopedView call the PDP makes for its card
   * (fetchAllSellers=false, plus the aggregator option code), for the size
   * picked on the wishlist. Wishlist only: the cart host has deliveryChoices.
   *
   * Skipped while the seller-list modal is up: that modal asks for the full
   * seller list itself and re-scopes the card on close, so firing this too would
   * mean two calls per size change — the rule the PDP follows in `selectSize`.
   */
  fetchB2cSlaIfNeeded = () => {
    const {
      b2cSellerEnabled,
      sellerListOpen = false,
      slaSkuId = '',
      slaPincode = '',
      itemData = {},
      wishlistPage = false,
      fetchB2cSla
    } = this.props;
    // Cart only ever renders the deliveryChoices payload (`cartB2cSlaOptions`),
    // which already covers every size of the option in one call — scopedView
    // would fetch a response nothing on this host reads.
    if (!wishlistPage) return;
    if (!b2cSellerEnabled || sellerListOpen || !slaSkuId || !fetchB2cSla) return;
    // Same pincode the card reads the response back under.
    fetchB2cSla(slaPincode, slaSkuId, itemData?.code || '');
  };

  /**
   * Wishlist SLA — driven exactly like the PDP.
   *
   * The size list writes the picked sku to `cart.size`; mirroring it into the
   * product slice through the PDP's own `selectSize` gives the SLA block the
   * same inputs it has on the PDP: `product.selectedSizeCode` is set and
   * `selectSize` fires the scopedView (v2) call for that sku. DeliverySLAContainer
   * then reads the response out of the store on its own.
   */
  syncWishlistSizeToPdpState = () => {
    const {
      wishlistPage = false,
      multiSellerSlaEnabled,
      size = '',
      sizeOptions = [],
      selectPdpSize,
      itemData = {}
    } = this.props;
    if (!wishlistPage || !multiSellerSlaEnabled || !size || !selectPdpSize) return;
    const { value: sizeLabel = '' } = sizeOptions.find(({ code }) => code === size) || {};
    // scopedView needs this product's option code. `selectSize` would otherwise
    // read it off product.productDetails, which here holds whatever PDP was last
    // visited (or nothing) — so hand it the wishlist entity's own code.
    selectPdpSize(sizeLabel, size, itemData?.code || '');
  };

  /**
   * Pull the delivery options for this cart line.
   *
   * deliveryChoices returns every size of the option in one payload, so a single
   * call on open covers the size the user is about to pick — no refetch per size
   * and no checkDeliveryDetails call from the cart.
   */
  fetchDeliveryChoicesIfNeeded = () => {
    const {
      multiSellerSlaEnabled,
      b2cSellerEnabled = false,
      code = '',
      fetchDeliveryChoices,
      wishlistPage = false,
      deliveryChoices
    } = this.props;
    // The wishlist path is on v2 (fetchWishlistSlaIfNeeded), not deliveryChoices.
    if (wishlistPage) return;
    // Already fetched by the host, which is what gated this popup's opening.
    if (Array.isArray(deliveryChoices)) return;
    // B2C reads this payload too, and its flag turns multiSellerSlaEnabled off
    // (see mapStateToProps), so the guard has to admit both cohorts.
    if ((!multiSellerSlaEnabled && !b2cSellerEnabled) || !code || !fetchDeliveryChoices) return;

    this.setState({
      cartDeliveryChoices: [],
      cartChoicesLoading: true,
      cartChoicesError: false
    });
    fetchDeliveryChoices({ productId: code })
      .then((response = []) => {
        this.setState({
          cartDeliveryChoices: Array.isArray(response) ? response : [],
          cartChoicesLoading: false,
          cartChoicesError: false
        });
      })
      .catch(() => {
        this.setState({
          cartDeliveryChoices: [],
          cartChoicesLoading: false,
          cartChoicesError: true
        });
      });
  };

  setSelectedSizeValue = () => {
    const { multiSellerSlaEnabled, b2cSellerEnabled = false, wishlistPage = false } = this.props;
    // Size change resets qty to 1 — seller/SLA options are size-scoped (B2C
    // and multiseller). Wishlist stays on its own add-to-bag quantity.
    if ((multiSellerSlaEnabled || b2cSellerEnabled) && !wishlistPage) {
      const qtyActuallyReset = Number(this.state.currentCount) > 1;
      this.setState({
        selectSize: true,
        currentCount: 1,
        userPriceType: '',
        userChangedSize: true,
        userChangedSeller: false,
        ...(b2cSellerEnabled ? { b2cSelectedOption: null } : {}),
        ...(qtyActuallyReset ? { userChangedQty: true } : {})
      });
      return;
    }
    // B2C wishlist: the sellers on offer belong to the size, so the pick goes
    // with it — the new size's scopedView response brings its own selected card.
    if (b2cSellerEnabled) {
      this.setState({ b2cSelectedOption: null, userChangedSeller: false });
    }
    this.setState({ selectSize: true, userChangedSize: true });
  };

  handleSlaSelect = option => {
    const { multiSellerSlaEnabled, b2cSellerEnabled = false, wishlistPage = false } = this.props;
    // B2C picks a seller here too, and its flag turns multiSellerSlaEnabled off
    // (see mapStateToProps), so the guard has to admit both cohorts.
    if ((!multiSellerSlaEnabled && !b2cSellerEnabled) || wishlistPage) return;
    // Multiseller: switching SLA/seller option resets qty to 1.
    // Count as Quantity Update only when qty actually drops from >1 to 1.
    const qtyActuallyReset = Number(this.state.currentCount) > 1;
    this.setState({
      selectSize: true,
      currentCount: 1,
      userPriceType: option?.rawPriceType || '',
      userSellerId: option?.sellerId || '',
      userChangedSeller: true,
      ...(qtyActuallyReset ? { userChangedQty: true } : {})
    });
  };

  changeQuantity(changeType = 'decrement') {
    const { newDesignLuxe, code = '', itemData = {}, wishlistPage = false } = this.props;
    if (changeType === 'decrement' && this.state.currentCount - 1 > 0) {
      this.setState({ currentCount: this.state.currentCount - 1, userChangedQty: true });
    } else if (changeType === 'increment' && this.state.currentCount + 1 <= 10) {
      this.setState({ currentCount: this.state.currentCount + 1, userChangedQty: true });
    } else {
      return;
    }

    // Cart-only quantity events — nothing is in the bag yet on the wishlist.
    if (wishlistPage) return;
    if (newDesignLuxe) {
      gtmEventPush({
        event: 'quanity_change_cart',
        event_category: 'bag interactions',
        event_action: 'quantity change',
        event_label: changeType,
        store_type: luxeStore,
        screen_name: screenName,
        screen_type: screenType
      });
    }
    segmentEventPush({
      event: 'Product Quantity Updated',    
      order_id : readCookie('C'),
      //order_status
      //previous_screen_name
      product_id : code,
      screen_name: 'Cart Page',
      products: [
        {
          ...getProductItemForCart(itemData)
        }
      ]
    })
  }

  updateProductForDataGlinch = () => {
    const { code = '', size, priceInfo = {} } = this.props;

    const { Price, Discount } = calculatePriceInfo(priceInfo);

    const { currentCount } = this.state;

    const eventObj = [];

    eventObj.ProductId = code;
    eventObj.NewQuantity = currentCount;
    eventObj.NewSize = size;

    eventObj.Price = Price;
    eventObj.Discount = Discount;

    const { pageId, pageType } = getPageInfo(location.pathname || '');
    eventObj.pageId = pageId;
    eventObj.pageType = pageType;
    sendEvent('ProductUpdate', eventObj);
    cleverTapPushEvent({
      eventName: CLEVERTAP_EVENT_NAMES.Change_Quantity,
      eventObj: {
        New_Quantity: currentCount
      }
    });
  };

  handleSlaSelect(option = {}) {
    this.setState({ slaSelectedPriceType: option?.rawPriceType || '' });
  }

  /**
   * Size picked inside the B2C seller-list modal. Runs the same pair the size
   * swatches do (EditSizeUpdate.setProductSize): mark the popup dirty, then write
   * the sku to `cart.size` so the modal strip and the popup underneath it agree.
   */
  handleB2cModalSizeSelect = sizeCode => {
    const { selectCartSize } = this.props;
    this.setSelectedSizeValue();
    if (selectCartSize) selectCartSize(sizeCode);
  };

  /**
   * Seller card picked on the B2C Delivery SLA card. Kept here rather than in the
   * product slice — this popup commits its own line, and the card it holds is the
   * one whose priceType + seller go with the cart / add-to-bag call.
   */
  handleB2cSlaSelect = (option = {}) => {
    const { wishlistPage = false } = this.props;
    const qtyActuallyReset = !wishlistPage && Number(this.state.currentCount) > 1;
    this.setState({
      b2cSelectedOption: option || null,
      // Same rule the multiseller card follows: a different seller option is an
      // edit, so the CTA comes alive and the change is named in the event.
      selectSize: true,
      userChangedSeller: true,
      ...(!wishlistPage ? { currentCount: 1 } : {}),
      ...(qtyActuallyReset ? { userChangedQty: true } : {})
    });
  };

  render() {
    const {
      quantity = 0,
      entryNumber = -1,
      code = '',
      modalName = '',
      size,
      sizeDetails = [],
      dealEnabled = false,
      newSizeRevampFlag,
      showBrandSizeFlag,
      beautyVolume = '',
      hideSizes = false,
      newDesignLuxe,
      itemData = {},
      multiSellerSlaEnabled = false,
      b2cSellerEnabled = false,
      slaSkuId = '',
      slaConfig = {},
      slaCodeLabels,
      wishlistPage = false,
      variantOptions = [],
      sizeOptions = [],
      wishlistSlaOptions = [],
      b2cSlaOptions = [],
      selectedSlaOption = null,
      activePincode = '',
      hasCartDeliveryLocation = false,
      hasDeliveryLocation = false,
      onB2cOtherSellers
    } = this.props;
    const {
      selectSize,
      currentCount,
      oldQuantity,
      cartDeliveryChoices,
      cartChoicesLoading,
      cartChoicesError,
      userPriceType,
      slaSelectedPriceType,
      b2cSelectedOption,
      userChangedSize,
      userChangedQty,
      userChangedSeller,
      userSellerId
    } = this.state;
    // Keys as defined on multiSellerSlaConfig — note depends on SLA card count.
    const { priceTypeTags } = slaConfig;

    // Cart reads the deliveryChoices payload it pulls on open — one call per
    // option covering every size — so changing size only re-reads what is in
    // hand. The wishlist renders the connected PDP container instead (see below),
    // which owns its own store reads.
    const selectedSkuId = slaSkuId;
    const { value: selectedSizeLabel } =
      sizeOptions.find(({ code: sizeCode }) => sizeCode === selectedSkuId) || {};
    const slaViewModel = withUserSlaSelection(
      multiSellerSlaEnabled && !wishlistPage && selectedSkuId
        ? mapDeliveryChoice(
            findDeliveryChoice(cartDeliveryChoices, selectedSkuId, selectedSizeLabel),
            priceTypeTags
          )
        : null,
      userPriceType
    );
    // B2C seller takes this slot whenever its flag is on, multiseller included —
    // the same precedence the desktop PDP gives it.
    const showB2cSla = Boolean(b2cSellerEnabled && (!wishlistPage || slaSkuId));
    // Cart B2C reads the same deliveryChoices payload the multiseller card
    // does — one call per option covering every size — so the seller cards, the
    // seller row and the sellers link all come off the API here. The wishlist
    // has no deliveryChoices call (fetchDeliveryChoicesIfNeeded bails on it) and
    // stays on the scopedView response in `b2cSlaOptions`, which
    // fetchB2cSlaIfNeeded fills for both hosts.
    const b2cChoice =
      showB2cSla && !wishlistPage
        ? findDeliveryChoice(cartDeliveryChoices, selectedSkuId, selectedSizeLabel)
        : undefined;
    const cartB2cAllSlaOptions = (b2cChoice?.deliveryDetails || []).map(detail =>
      toRawSlaOption(detail, b2cChoice)
    );
    // Cards on this popup: the default-display sellers only. The rest are
    // reached through "Other sellers available", which opens the full list. A
    // payload that marks none falls back to all, so the card is never empty.
    const cartB2cDefaultSlaOptions = cartB2cAllSlaOptions.filter(option => option?.defaultDisplay);
    // The API marks at most two, so the cap is only a guard against a payload
    // that marks more (or none, where the first two stand in) — this popup is
    // never meant to grow past two cards.
    const cartB2cSlaOptions = withUserB2cRawSelection(
      (cartB2cDefaultSlaOptions.length
        ? cartB2cDefaultSlaOptions
        : cartB2cAllSlaOptions
      ).slice(0, B2C_MAX_DEFAULT_SELLERS),
      b2cSelectedOption
    );
    // One source per host, so the cards, the seller row and the update payload
    // can never disagree about which sellers are on offer.
    const b2cShownSlaOptions = wishlistPage ? b2cSlaOptions : cartB2cSlaOptions;
    const b2cSelectedSeller =
      b2cShownSlaOptions.find(option => option?.isSelected) || b2cShownSlaOptions[0] || {};
    /* What the card renders. The wishlist hands over nothing, leaving the
     * container on its own scopedView read for `skuId` — the only response that
     * host has. The cart owns its deliveryChoices cards.
     * TODO - Remove this mock data used (the fallback). */
    let b2cCardSlaOptions;
    if (!wishlistPage) {
      b2cCardSlaOptions = cartB2cSlaOptions.length ? cartB2cSlaOptions : [];
    }
    // Catalogue seller for the picked size, off the wishlist listing payload.
    // Only used with no delivery location, where scopedView never runs and so
    // carries no seller of its own.
    const noLocationSellerName = sellerNameForSize(variantOptions, slaSkuId, itemData?.sellerName);
    const showSla = Boolean(
      hasCartDeliveryLocation &&
        slaViewModel &&
        (cartChoicesLoading || cartChoicesError || slaViewModel.options.length > 0)
    );
    // Wishlist: the PDP container renders (and self-gates on) the SLA for the
    // sku in `product.selectedSizeCode`, which syncWishlistSizeToPdpState sets.
    const showWishlistSla = Boolean(wishlistPage && multiSellerSlaEnabled && !showB2cSla);
    // Bag glyph on the CTA, matching the B2C seller-list modal this popup opens.
    // Wishlist only: the cart's button says "Update" — the line is in the bag
    // already — and the non-B2C popup keeps its plain text CTA.
    // Reads the flag, not `showB2cSla`: the CTA must not change shape halfway
    // through, when the size that reveals the card is picked.
    const showB2cAddToBag = Boolean(wishlistPage && b2cSellerEnabled);
    // Cart only — the wishlist takes its note from DeliverySLAContainer (below),
    // which owns the mapping that decides how many cards actually render.
    const deliveryNote =
      pickSlaDeliveryNote(slaConfig, slaViewModel?.options?.length || 0) || SLA_DELIVERY_NOTE;
    let disableUpdateButton = true;

    // A different seller option (SLA) picked in the modal also counts as a change.
    const slaChanged =
      Boolean(slaSelectedPriceType) &&
      slaSelectedPriceType !== (itemData?.selectedPriceType || '');

    // Nothing is preselected on the wishlist, so a size is always required
    // before the item can go into the bag — a quantity bump alone is not enough.
    if (wishlistPage ? selectSize : selectSize || currentCount !== oldQuantity || slaChanged) {
      disableUpdateButton = false;
    }

    /* Multiseller and B2C carts: sizes + stock come from deliveryChoices, not
     * sizeVariants (that call is skipped for both). Reshaped into the
     * sizeVariants baseOptions shape so the swatch/stock code below and
     * ProductSizeUpdation stay untouched. */
    const useDeliveryChoiceSizes = (multiSellerSlaEnabled || b2cSellerEnabled) && !wishlistPage;
    const effectiveSizeDetails = useDeliveryChoiceSizes
      ? {
          baseOptions: [
            {
              variantType: 'FnlSizeVariant',
              options: deliveryChoicesToSizeVariants(cartDeliveryChoices),
              selected: {
                variantOptionQualifiers:
                  sizeVariantSelectedOf(
                    findDeliveryChoice(cartDeliveryChoices, selectedSkuId, selectedSizeLabel)
                  )?.variantOptionQualifiers || []
              }
            }
          ]
        }
      : sizeDetails;

    let sizeVariantOptions = [];
    (effectiveSizeDetails.baseOptions || []).forEach((variant, i) => {
      if (variant.variantType === 'FnlSizeVariant') {
        sizeVariantOptions = variant.options;
      }
    });
    const productList = [];
    sizeVariantOptions.forEach(({ code, stock }) => {
      productList.push({ code, stock });
    });
    const [{ stock: { stockLevel: sizeStockLevel } = {} } = {}] = (productList || []).filter(
      ({ code: productCode }) => productCode === size
    );
    /* Multiseller cart: availableQuantity lives on each deliveryDetails entry, so
     * the stock shown ("n left"), the qty ceiling and the update button follow the
     * selected delivery card — not the size as a whole. Flag off / wishlist (v2)
     * path / no options: the size-level stock stands, as before. */
    const slaStockLevel = useDeliveryChoiceSizes
      ? slaStockLevelOf(slaViewModel, userPriceType)
      : undefined;
    const stockLevel = slaStockLevel === undefined ? sizeStockLevel : slaStockLevel;
    if (stockLevel < currentCount) {
      disableUpdateButton = true;
    }

    let isSelectedSizeIsOOS = false;
    if (sizeVariantOptions && sizeVariantOptions.length) {
      const cartStockCheck =
        sizeVariantOptions.find(({ code: sizeCode }) => sizeCode === size) || {};
      if (cartStockCheck && cartStockCheck.stock) {
        const { stockLevel: stock } = cartStockCheck.stock;
        if (stock === 0) {
          isSelectedSizeIsOOS = true;
        }
      }
    }

    let isProductIsOOS = false;
    if (sizeVariantOptions && sizeVariantOptions.length) {
      isProductIsOOS = sizeVariantOptions.every(({ stock: { stockLevel } }) => stockLevel === 0);
    }
    if (isProductIsOOS) {
      disableUpdateButton = true;
    }

    /* B2C seller: the seller-list modal describes this popup's own line, so its
     * product is built here instead of being read off the PDP `product` slice —
     * that slice holds whatever PDP was last visited. Sizes come from the
     * wishlist's mapped list when the host passes one, else from the size
     * variants this popup already resolved (cart). */
    const b2cModalSizes = sizeOptions.length
      ? sizeOptions.map(({ code: sizeCode, value, stockLevel: sizeStock }) => ({
          code: sizeCode,
          label: value,
          outOfStock: sizeStock === 0
        }))
      : sizeVariantOptions.map(option => ({
          code: option?.code,
          label: sizeLabelOf(option),
          outOfStock:
            option?.stock?.stockLevel === 0 || option?.stock?.stockLevelStatus === 'outOfStock'
        }));
    const b2cModalProduct = {
      // The wishlist entity nests the brand; a cart line carries it flat.
      brandName: itemData?.fnlColorVariantData?.brandName || itemData?.brandName || '',
      productName: itemData?.name || '',
      imageUrl: popupThumbnailOf(itemData),
      optionCode: itemData?.code || '',
      sizes: b2cModalSizes,
      selectedSizeCode: size || '',
      // Carried over so a host that owns a quantity (the cart line) can keep the
      // stepper on whatever the user had set in here.
      quantity: currentCount,
      // Same pair this popup hands the SLA card, so "Delivery And Return Details
      // Viewed" off the seller list names this line rather than the PDP slice.
      productItem: getProductItemForCart(itemData),
      // The cart host is handed no `sizeOptions`, so the label comes off the
      // deliveryChoices entry this popup is showing.
      sizeName: b2cChoice?.size?.value || selectedSizeLabel || ''
    };

    /**
     * The popup's "Update" / "Add to Bag", also driven by the B2C seller-list
     * modal's own CTA — which hands back the sku and the seller picked in there
     * rather than the ones selected on the popup behind it.
     */
    const submitUpdate = ({
      sizeCode = '',
      selectedPriceType: pickedPriceType = '',
      sellerId: chosenSellerId = ''
    } = {}) => {
      const effectiveSize = sizeCode || size;
      // Always the raw API priceType — never the normalized view-model
      // key, which collapses anything but BEST_SPEED/FASTEST to "best".
      /**
       * The API's own priceType, never the view model's.
       *
       * A view model always has `priceType` set — `normalizePriceType` defaults
       * anything it does not recognise to "best", empty included — so falling
       * back to it invented a priceType for options that carry none. `??` picks
       * that apart: a view model always defines `rawPriceType` (blank when the
       * API sent nothing), a raw option has only `priceType`.
       */
      const rawPriceTypeOf = option => option?.rawPriceType ?? option?.priceType ?? '';
      const cartSelectedPriceType =
        userPriceType ||
        slaSelectedPriceType ||
        rawPriceTypeOf(
          slaViewModel?.options?.find(option => option.isSelected && option.serviceable)
        ) ||
        rawPriceTypeOf(slaViewModel?.options?.find(option => option.serviceable)) ||
        '';
      const wishlistSelectedPriceType =
        selectedSlaOption?.priceType ||
        (wishlistSlaOptions.find(option => option?.isSelected) || wishlistSlaOptions[0] || {})
          .priceType ||
        '';
      /* B2C: the card on screen speaks for the line — the user's pick, else the
       * one scopedView marked selected, else the first serviceable row. Both its
       * raw priceType and its seller go to the cart APIs, which is what the
       * seller-list modal hands back when the CTA in there is the one used. */
      const b2cOption =
        b2cSelectedOption ||
        b2cShownSlaOptions.find(option => option?.isSelected && isSlaOptionServiceable(option)) ||
        b2cShownSlaOptions.find(isSlaOptionServiceable) ||
        // Nothing flagged either way: the cards highlight the first row, so this
        // names the seller that is actually on screen rather than none at all.
        b2cShownSlaOptions[0] ||
        // Wishlist: the card is store-driven (it is handed no `slaOptions`), so
        // its pick lands in `edd.selectedSlaOption` — the only place the option
        // exists when this popup's own read of the slice comes back empty.
        (b2cSellerEnabled && wishlistPage ? selectedSlaOption : null) ||
        {};
      // scopedView sends the priceType padded (" BEST_PRICE "), so trim it.
      const b2cSelectedPriceType = String(rawPriceTypeOf(b2cOption)).trim();
      const selectedSellerId =
        chosenSellerId || (b2cSellerEnabled ? b2cOption?.sellerId || '' : '');
      const hostSelectedPriceType = wishlistPage
        ? wishlistSelectedPriceType
        : cartSelectedPriceType;
      const selectedPriceType =
        pickedPriceType || (b2cSellerEnabled ? b2cSelectedPriceType : hostSelectedPriceType);
      const bagUpdateReasons = [];
      if (userChangedSize) bagUpdateReasons.push('Size Update');
      if (userChangedQty) bagUpdateReasons.push('Quantity Update');
      if (userChangedSeller) bagUpdateReasons.push('seller option update');
      const { value: effectiveSizeLabel = '' } =
        sizeOptions.find(({ code: sizeCode }) => sizeCode === effectiveSize) || {};
      const updateEntryObj = {
        entryNumber,
        newQty: currentCount,
        code,
        size: beautyVolume ? code : effectiveSize,
        actualSize: effectiveSizeLabel,
        itemData,
        // Both cohorts now price the line off a real scopedView response, so the
        // picked option travels with the update — B2C adds the seller it belongs
        // to, which its cart APIs require.
        /*
         * Each field stands on its own. They used to share one `&& selectedPriceType`
         * gate, so a B2C card whose scopedView row carries no priceType (the add
         * made straight off the popup, with no pick of the user's own) dropped the
         * seller and the option list along with it — and on B2C it is the seller,
         * not the priceType, that names the option.
         */
        ...((multiSellerSlaEnabled || b2cSellerEnabled) && selectedPriceType
          ? { selectedPriceType }
          : {}),
        ...(b2cSellerEnabled && selectedSellerId ? { selectedSellerId } : {}),
        ...((multiSellerSlaEnabled || b2cSellerEnabled) && wishlistPage
          ? { slaOptions: b2cSellerEnabled ? b2cShownSlaOptions : wishlistSlaOptions }
          : {}),
        /**
         * The card the line speaks for, as the raw API option "Add To Bag
         * Clicked" reports its seller / delivery props from. A pick made in the
         * All Sellers sheet is in none of the option arrays above — that sheet is
         * a separate fetchAllSellers response — so without this the event named
         * the seller scopedView had pre-selected instead of the chosen one.
         */
        ...(b2cSellerEnabled && b2cOption ? { pickedSlaOption: b2cOption.raw || b2cOption } : {}),
        ...(b2cSellerEnabled && selectedSellerId ? { sellerId: selectedSellerId } : {}),
        bagUpdateReason: bagUpdateReasons.join(',')
      };
      const { updateQuantity } = this.props;
      if (wishlistPage) {
        updateQuantity(modalName, updateEntryObj);
        return;
      }
      updateQuantity(modalName, updateEntryObj)
        .then(() => this.updateProductForDataGlinch(true))
        .catch(() => this.updateProductForDataGlinch(false));
      // eslint-disable-next-line no-undef
      ga('send', {
        hitType: 'event',
        eventCategory: 'Buttop tap',
        eventAction: 'Current event for clicking on "update" button ',
        eventLabel: 'Inventory change'
      });
    };

    // The wishlist opens this popup through a portal on document.body, outside the
    // SIS layout div that carries `luxe-newdesign store-in-store`. Repeating both
    // classes here lets the luxe theme (button colour, fonts, swatches) apply the
    // same way it does inside the cart.
    const luxeThemeClass = wishlistPage && newDesignLuxe ? 'luxe-newdesign store-in-store' : '';
    return (
      <div className={luxeThemeClass}>
        <div id="cardSizePopup">
          {/* Multiseller: deliveryChoices also decides which sizes are orderable.
              Passed empty on the wishlist (v2 path) and with the flag off, which
              leaves the swatches purely stock-driven. */}
          <ProductSizeUpdation
            code={code}
            setSelectedSizeValue={this.setSelectedSizeValue}
            size={size}
            sizeDetails={effectiveSizeDetails}
            variantOptions={variantOptions}
            newSizeRevampFlag={newSizeRevampFlag}
            showBrandSizeFlag={showBrandSizeFlag}
            hideSizes={beautyVolume || hideSizes}
            newDesignLuxe={newDesignLuxe}
            deliveryChoices={multiSellerSlaEnabled || b2cSellerEnabled ? cartDeliveryChoices : []}
            enableMultiSellerSla={multiSellerSlaEnabled}
          />
        </div>
        <div id="cardQtyPopup" className={showB2cSla ? 'cardQtyPopup--b2c' : undefined}>
          {/* Wishlist adds a single unit, so the quantity stepper is size-only there. */}
          {!wishlistPage &&
            (!dealEnabled ? (
              <div className="cartqty-section">
              <p className="header-label" role="heading" aria-level={2} id="quantity-group-label">
                {this.textContent.selectQuantity}
              </p>

            <div role="group" aria-labelledby="quantity-group-label">
              <button
                className={newDesignLuxe ? 'luxe-newdesign-decrement button' : 'decrement button'}
                onClick={() => this.changeQuantity('decrement')}
                disabled={isProductIsOOS || currentCount <= this.config.min}
                aria-label="Decrease quantity"
              >
                {' '}
                -{' '}
              </button>

              <div
                className={newDesignLuxe ? 'luxe-newdesign-counter' : 'counter'}
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {' '}
                {currentCount}{' '}
              </div>

              <button
                className={newDesignLuxe ? 'luxe-newdesign-increment button' : 'increment button'}
                onClick={() => this.changeQuantity('increment')}
                disabled={
                  isProductIsOOS ||
                  currentCount + 1 >
                    (stockLevel !== 'undefined'
                      ? Math.min(this.config.max, stockLevel)
                      : this.config.max)
                }
                aria-label="Increase quantity"
              >
                {' '}
                +{' '}
              </button>
            </div>
              {stockLevel !== undefined && stockLevel >= currentCount && stockLevel <= 10 && (
                <p className="available-error-msg">
                  <span className="availablily-message">{stockLevel} left </span>
                </p>
              )}
              {!!stockLevel && stockLevel < currentCount && (
                <p className="available-error-msg">
                  <span className="availablily-message">
                    Please update the quantity, only {stockLevel} left{' '}
                  </span>
                </p>
              )}
              {stockLevel !== undefined && stockLevel === 0 && !isSelectedSizeIsOOS && (
                <p className="available-error-msg">
                  <span className="availablily-message">This product is out of stock </span>
                </p>
              )}
            </div>
            ) : (
              <div className="dealSizeChangeMessage">Price may vary according to size</div>
            ))}
          {/* Wishlist with no delivery location: scopedView never runs, so there
              are no cards and no heading to sit above them — just the catalogue
              seller for the picked size, per the B2C design. Wishlist-only
              because the catalogue seller comes off `variantOptions`, which only
              that host passes; the cart line's seller is on its own entry. */}
          {showB2cSla && wishlistPage && !hasDeliveryLocation && (
            <div className="cart-delivery-sla">
              <B2cSellerNoLocation
                sellerName={noLocationSellerName}
                luxe={newDesignLuxe}
                onDialogSurface
              />
            </div>
          )}
          {/* B2C seller — wins this slot over both SLA branches below. The card
              carries its own seller row, "Other sellers available" link and
              delivery note, so the heading here is plain: no info tooltip. */}
          {showB2cSla && !(wishlistPage && !hasDeliveryLocation) && (
            <div className="cart-delivery-sla">
              <p className="cart-delivery-sla__heading" role="heading" aria-level={2}>
                Delivery Detail
              </p>
              <B2cSellerDeliverySLA
                hideHeader
                luxe={newDesignLuxe}
                // Names the screen on "Delivery And Return Details Viewed";
                // the line and its size are this popup's, not the PDP slice's.
                surface={wishlistPage ? SLA_SURFACE.WISHLIST_SHEET : SLA_SURFACE.BAG_SHEET}
                productItem={getProductItemForCart(itemData)}
                // The cart host is handed no `sizeOptions`, so the label comes
                // off the deliveryChoices entry this popup is showing.
                sizeSelectedName={b2cChoice?.size?.value || selectedSizeLabel}
                // The scopedView response for this popup's own sku — the cart
                // line's, or the size picked on the wishlist.
                skuId={slaSkuId}
                onSelectOption={this.handleB2cSlaSelect}
                // Cart only — the wishlist leaves the container on its own
                // scopedView read (see b2cCardSlaOptions).
                slaOptions={b2cCardSlaOptions}
                // TODO - Remove this mock data used (the || fallback)
                // The user's pick first: `b2cSelectedSeller` reads the API's own
                // `isSelected`, which does not move when another card is picked,
                // and a host-supplied name wins over the card's internal one.
                sellerName={b2cSelectedOption?.sellerName || b2cSelectedSeller?.sellerName}
                // Seller-list modal shows every seller, not just the two on the
                // cards, so it gets the unfiltered set.
                allSellerOptions={wishlistPage ? undefined : cartB2cAllSlaOptions}
                hasMoreSellers={
                  wishlistPage ? undefined : Boolean(b2cChoice?.hasMoreSellers)
                }
                modalProduct={b2cModalProduct}
                onSelectModalSize={this.handleB2cModalSizeSelect}
                onAddToBag={(sizeCode, selectedPriceType, sellerId) =>
                  submitUpdate({ sizeCode, selectedPriceType, sellerId })
                }
                // A host that wants this popup gone before the seller modal
                // opens has to own the modal too — closing this one unmounts
                // everything inside it, the modal included.
                renderSellerModal={!onB2cOtherSellers}
                onOtherSellersClick={
                  onB2cOtherSellers
                    ? // The host renders the modal (this popup closes first), so
                      // the sellers it fetched have to travel with the product —
                      // the host has no deliveryChoices call of its own.
                      () => onB2cOtherSellers(b2cModalProduct, cartB2cAllSlaOptions)
                    : undefined
                }
              />
            </div>
          )}
          {showSla && (
            <div className="cart-delivery-sla">
              <p className="cart-delivery-sla__heading" role="heading" aria-level={2}>
                Delivery Detail
                {deliveryNote && (
                  <span
                    className="cart-delivery-sla__info-wrap"
                    tabIndex={0}
                    role="button"
                    aria-label={deliveryNote}
                  >
                    <img
                      src={newDesignLuxe ? INFO_ICON_LUXE : INFO_ICON}
                      alt=""
                      aria-hidden="true"
                      className="cart-delivery-sla__info"
                    />
                    <span className="cart-delivery-sla__arrow" aria-hidden="true" />
                  </span>
                )}
                {/* Sibling of the icon, not a child: the bubble spans the
                    heading so it stays inside the modal instead of hanging
                    off the icon and spilling past the right edge. */}
                {deliveryNote && (
                  <span className="cart-delivery-sla__tooltip" role="tooltip">
                    {deliveryNote}
                  </span>
                )}
              </p>
              <DeliverySLA
                viewModel={slaViewModel}
                loading={cartChoicesLoading}
                error={cartChoicesError}
                slaCodeLabels={slaCodeLabels}
                hideHeader
                footerNote=""
                luxe={newDesignLuxe}
                onSelectOption={this.handleSlaSelect}
                perPiece
                activePincode={activePincode}
              />
            </div>
          )}
          {showWishlistSla && (
            <div className="cart-delivery-sla">
              {/* The heading rides along as `title` so it appears with the
                  response — not while the call is still in flight. Taking `note`
                  from the container ties the tooltip copy to the cards it just
                  rendered; deriving it here instead let the two disagree, since
                  mapSlaOptions dedupes by priceType and this component only sees
                  the raw options. */}
              <DeliverySLAContainer
                hideHeader
                luxe={newDesignLuxe}
                footerNote=""
                title={note => {
                  const wishlistNote = note || SLA_DELIVERY_NOTE;
                  return (
                    <p className="cart-delivery-sla__heading" role="heading" aria-level={2}>
                      Delivery Detail
                      {wishlistNote && (
                        <span
                          className="cart-delivery-sla__info-wrap"
                          tabIndex={0}
                          role="button"
                          aria-label={wishlistNote}
                        >
                          <img
                            src={newDesignLuxe ? INFO_ICON_LUXE : INFO_ICON}
                            alt=""
                            aria-hidden="true"
                            className="cart-delivery-sla__info"
                          />
                          <span className="cart-delivery-sla__arrow" aria-hidden="true" />
                        </span>
                      )}
                      {wishlistNote && (
                        <span className="cart-delivery-sla__tooltip" role="tooltip">
                          {wishlistNote}
                        </span>
                      )}
                    </p>
                  );
                }}
              />
            </div>
          )}
          {!isProductIsOOS ? (
            <button
              className="full-width-button"
              id="updateQuantity"
              disabled={disableUpdateButton}
              tabIndex={disableUpdateButton ? -1 : 0}
              onClick={() => submitUpdate()}
            >
              {showB2cAddToBag && <span className="ic-pdp-add-cart" aria-hidden="true" />}
              {wishlistPage ? 'Add to Bag' : 'Update'}
            </button>
          ) : (
            <button
              className="full-width-button"
              id="updateQuantity"
              onClick={() => {
                window.location.reload();
              }}
            >
              OKAY
            </button>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps = {}) => {
  const { cart = {}, flags = {} } = state;
  const { size, sizeDetails } = cart;
  const { isDesktopThemeEnable } = getLuxeFlags(flags);
  const storeName = getCurrentStore(state);
  const newDesignLuxe = isDesktopThemeEnable === 'ON' && storeName === 'luxe';
  // B2C ships its own slaTimeMessage / priceTypeTags / deliverySlaNote, so the
  // popup's SLA copy follows whichever flag is driving the cards.
  const slaConfig = getB2cSellerFlag(state)
    ? getB2cSellerConfig(state)
    : getMultiSellerSlaConfig(state);
  // `cart.size` is only populated once the size list inside the popup mounts, so
  // it is empty on the first render of the quantity flow. The cart line already
  // knows its SKU, so use that until a different size is picked — otherwise the
  // SLA block never renders when the popup is opened from "Qty". Not applied on
  // the wishlist, where no size is selected yet and `code` is the base product.
  const { code: cartItemCode = '', wishlistPage = false } = ownProps;
  const slaSkuId = wishlistPage ? size : size || cartItemCode;
  // Same pincode the SLA slice is keyed by — the wishlist selects options with
  // it, and the cart cards name it in the "not deliverable" copy.
  const activePincode = state?.edd?.activePincode;
  // Somewhere to deliver to: a selected address, or failing that the bare
  // `pinCode` the cart response carries at its root. With neither there is
  // nothing to quote delivery against, so the SLA block stays hidden.
  // deliveryAddress is always present on the normalized cart and defaults to {},
  // hence the emptiness check rather than a plain existence one.
  // Same guard fetchDeliveryDetailsV2 applies, so the B2C card only waits on a
  // response that is actually coming. Without a location there is none, and the
  // popup names the catalogue seller instead — see B2cSellerNoLocation. Falls
  // back to the cookie exactly as the thunk does, so both agree on the pincode.
  const hasDeliveryLocation = hasVisibleDeliveryLocation(
    state,
    activePincode || getPincodeFromCookie()
  );
  const hasCartDeliveryLocation =
    Object.keys(state?.cart?.deliveryInfo?.deliveryAddress || {}).length > 0 ||
    Boolean(state?.cart?.pinCode);
  const b2cSellerEnabled = getB2cSellerFlag(state);
  const wishlistSizeCode = state?.product?.selectedSizeCode || '';
  // Same fallback fetchDeliveryDetailsV2 applies, so the key the response is
  // stored under and the key read back here cannot diverge.
  const slaPincode = activePincode || getPincodeFromCookie();
  const wishlistSlaOptions = wishlistPage
    ? selectSlaOptions(state, slaPincode, wishlistSizeCode)
    : [];
  // B2C reads its own sku (the cart line's, or the size picked in here) rather
  // than the PDP slice, so nothing outside this popup has to be kept in step.
  // Wishlist only — the cart host reads its cards off deliveryChoices and makes
  // no scopedView call of its own (see fetchB2cSlaIfNeeded).
  const b2cSlaOptions =
    b2cSellerEnabled && wishlistPage ? selectSlaOptions(state, slaPincode, slaSkuId) : [];
  return {
    newDesignLuxe,
    activePincode,
    slaPincode,
    hasCartDeliveryLocation,
    hasDeliveryLocation,
    size,
    sizeDetails,
    newSizeRevampFlag: getNewSizeRevampFlag(flags),
    showBrandSizeFlag: getShowBrandSizeFlag(flags),
    // B2C wins outright: with its flag on the popup is the B2C one on every
    // host, so multiseller is switched off at the source rather than gated at
    // each of its call sites. That takes the deliveryChoices call, the
    // size-from-deliveryChoices list, the size/seller qty resets and the
    // selectedPriceType on update out of play, leaving the cart popup on the
    // same mock-data path the wishlist already runs.
    multiSellerSlaEnabled: getMultiSellerSlaFlag(state) && !b2cSellerEnabled,
    b2cSellerEnabled,
    slaConfig,
    slaSkuId,
    wishlistSlaOptions,
    b2cSlaOptions,
    // The seller-list modal owns the calls while it is up — see fetchB2cSlaIfNeeded.
    sellerListOpen: Boolean(state?.modals?.[ModalTypes.B2C_SELLER_LIST]),
    selectedSlaOption: state?.edd?.selectedSlaOption || null,
    // SLA copy lives on the multiseller flag alongside the rest of this feature's
    // config, so it covers every code the APIs emit rather than only the ones
    // priorityDeliverySLPTagsConfig happens to carry.
    slaCodeLabels: slaConfig?.slaTimeMessage
  };
};

const mapDispatchToProps = dispatch => {
  return {
    hideModal: name => {
      dispatch(hideModal(name));
    },
    fetchDeliveryChoices: payload => dispatch(getDeliveryChoices(payload)),
    // B2C Delivery SLA card — the PDP's own call (scopedView, fetchAllSellers=false
    // plus the aggregator option code), scoped to this popup's sku. The pincode is
    // left to the thunk, which defaults to the cookie one the slice is keyed by.
    fetchB2cSla: (pincode, sizeCode, optionCode) =>
      dispatch(fetchDeliveryDetailsV2(pincode || undefined, sizeCode, 1, false, optionCode)),
    // Same action the size swatches dispatch — keeps `cart.size` the single
    // source of the picked sku when the B2C seller modal is the one picking it.
    selectCartSize: sizeCode => dispatch(selectProductSize(sizeCode)),
    // Same action the PDP size selector dispatches: it sets
    // product.selectedSizeCode and fires the scopedView (v2) call for that sku.
    selectPdpSize: (sizeName, sizeCode, optionCode = '') =>
      dispatch(
        selectPdpSizeAction(
          sizeName,
          sizeCode,
          undefined,
          undefined,
          undefined,
          undefined,
          optionCode
        )
      )
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductUpdationPopup);
